import React, { useEffect, useRef, useState } from "react";
import MotionMedia from "./MotionMedia";
import "./CinematicServiceHero.css";
import { getMedia } from "../data/mediaRegistry";

const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Reference-inspired cinematic service hero (White Desert scale + Sobha
 * restraint + brief ERA-style aperture reveal), kept inside the Time
 * Furniture cream/burgundy/serif identity.
 * - ~full viewport under the fixed header; immediate stable poster; the
 *   semantic H1/support/CTA markup is always in the DOM (visible without JS
 *   in prerendered HTML; animations only ever ADD polish).
 * - Entrance ONCE: rectangular inset clip reveal → full bleed (~900ms) with
 *   image scale 1.04→1; heading/support/CTA blocks stagger 20px / 600ms.
 * - Native scroll: very mild parallax + text fade within the first hero
 *   height. CTA block never fades while focused. No scroll-jacking, no
 *   loaders, no WebGL.
 * - Mobile: reduced movement. prefers-reduced-motion: everything immediate.
 */
export default function CinematicServiceHero({
  mediaKey,
  mobileFullFrame = false,
  posterAlt = "",
  eyebrow,
  sectionNumber = "01",
  h1,
  support,
  children,
  testid = "cinematic-hero",
  cueLabel = "Explore the design",
}) {
  const secRef = useRef(null);
  const contentRef = useRef(null);
  const [entered, setEntered] = useState(false);
  const [instant, setInstant] = useState(false);
  const [progress, setProgress] = useState(0);
  const [focusWithin, setFocusWithin] = useState(false);
  const hasVideo = Boolean((getMedia(mediaKey) || {}).video);

  useEffect(() => {
    if (reduced()) {
      setInstant(true);
      setEntered(true);
      return;
    }
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (reduced()) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = secRef.current ? secRef.current.offsetHeight : window.innerHeight;
        setProgress(Math.min(Math.max(window.scrollY / Math.max(h, 1), 0), 1));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const inset = entered ? "inset(0% 0% 0% 0%)" : isMobile ? "inset(5% 4% 5% 4%)" : "inset(12% 8% 12% 8%)";
  const mediaScale = entered ? 1 : 1.04;
  const parallax = instant ? 0 : progress * (isMobile ? 14 : 36);
  const textOpacity = focusWithin || instant ? 1 : Math.max(1 - progress * 1.15, 0);

  const blockStyle = (i) =>
    instant
      ? undefined
      : {
          opacity: entered ? 1 : 0,
          transform: entered ? "translateY(0)" : "translateY(20px)",
          transition: `opacity 600ms ease ${150 * i}ms, transform 600ms ease ${150 * i}ms`,
        };

  return (
    <section
      ref={secRef}
      data-testid={testid}
      className={`relative overflow-hidden min-h-[560px] h-[calc(100svh-80px)] bg-[#1C110C] ${mobileFullFrame ? "cinematic-full-frame-mobile" : ""}`}
    >
      <div
        className="cinematic-hero-film absolute inset-0 will-change-transform"
        style={
          instant
            ? undefined
            : {
                clipPath: inset,
                transform: `scale(${mediaScale}) translateY(${parallax}px)`,
                transition: "clip-path 900ms cubic-bezier(0.33,1,0.68,1), transform 900ms cubic-bezier(0.33,1,0.68,1)",
              }
        }
      >
        <MotionMedia
          mediaKey={mediaKey}
          alt={posterAlt}
          mode="hero"
          fit={mobileFullFrame ? "contain" : "cover"}
          toggleClass={mobileFullFrame ? "bottom-4 left-4 lg:bottom-auto lg:left-auto lg:top-[72px] lg:right-5" : null}
          aspectClass="h-full w-full"
          testid={`${testid}-media`}
          eager
        />
      </div>
      {/* subtle left/bottom gradient for readable typography */}
      <div className="cinematic-hero-shade absolute inset-0 bg-gradient-to-r from-[#1C110C]/70 via-[#1C110C]/25 to-transparent pointer-events-none" />
      <div className="cinematic-hero-shade absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#1C110C]/80 to-transparent pointer-events-none" />

      {/* Mobile-only wordmark: portrait object-cover crops the baked top-right
          mark out of the wide films, so branding is restored here. Desktop
          keeps the baked mark only (no duplicate). */}
      <span
        aria-hidden="true"
        data-testid={`${testid}-mobile-wordmark`}
        className="cinematic-hero-mobile-wordmark sm:hidden pointer-events-none select-none absolute top-5 right-5 z-20 font-serif-display text-[13px] tracking-[0.3em] uppercase text-white opacity-20"
      >
        TIME · FURNITURE
      </span>

      <div
        ref={contentRef}
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocusWithin(false); }}
        className="cinematic-hero-content relative z-10 h-full flex items-end pointer-events-none"
        style={{ opacity: textOpacity }}
      >
        <div className="cinematic-hero-copy max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-24 w-full pointer-events-auto">
          <div style={blockStyle(0)} className="flex items-center gap-4 mb-6">
            <span className="text-[10px] tracking-[0.4em] text-[#D4A963]/80 font-medium">{sectionNumber}</span>
            <span className="h-px w-10 bg-[#D4A963]/50" />
            <p className="text-[11px] tracking-[0.36em] uppercase text-[#D4A963] font-medium">{eyebrow}</p>
          </div>
          <div style={blockStyle(1)}>{h1}</div>
          {support && <div style={blockStyle(2)}>{support}</div>}
          {children && <div style={blockStyle(3)}>{children}</div>}
        </div>
      </div>

      {/* scroll cue */}
      <div
        aria-hidden="true"
        className="cinematic-hero-scroll-cue absolute bottom-6 right-6 md:right-12 z-10 hidden sm:flex flex-col items-center gap-2 text-[#EDE1C8]/70"
        style={{ opacity: textOpacity }}
      >
        <span className="text-[9px] tracking-[0.3em] uppercase [writing-mode:vertical-rl]">{cueLabel}</span>
        <span className={`w-px h-10 bg-gradient-to-b from-[#D4A963] to-transparent ${instant ? "" : "animate-pulse"}`} />
      </div>

      {/* thin burgundy/gold detail into the cream content */}
      <div className="absolute bottom-0 inset-x-0 h-[3px] bg-gradient-to-r from-[#6E1F2C] via-[#D4A963] to-[#6E1F2C] z-10" />
    </section>
  );
}
